import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';

import { BenchwiseSearchwiseResponse } from '../../../../generated/model/benchwiseSearchwiseResponse';

interface Message {
  role: 'user' | 'bot';
  content: string;
}

export interface AssistHistoryItem {
  instructions: string;
  response: BenchwiseSearchwiseResponse;
  uid?: string;
  feedbackSent?: boolean; // added
}

interface AssistContextType {
  input: string;
  setInput: (input: string) => void;
  messages: Message[];
  setMessages: (msgs: Message[]) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  previousInstructions: string[];
  setPreviousInstructions: (instructions: string[]) => void;
  history: AssistHistoryItem[];
  setHistory: (history: AssistHistoryItem[]) => void;
}

const AssistContext = createContext<AssistContextType | undefined>(undefined);

export const useAssistContext = () => {
  const ctx = useContext(AssistContext);
  if (!ctx) throw new Error('useAssistContext must be used within AssistProvider');
  return ctx;
};

const LOCAL_STORAGE_KEY = 'companiesFilterAssist';

function loadPersistedState() {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return undefined;
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

function persistState(state: Partial<AssistContextType>) {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // do not do anything, intentionally
  }
}

export const CompaniesFilterAssistProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const persisted = loadPersistedState() || {};
  const [input, setInput] = useState<string>(persisted.input ?? '');
  const [messages, setMessages] = useState<Message[]>(persisted.messages ?? []);
  const [loading, setLoading] = useState<boolean>(false); // don't persist loading
  const [previousInstructions, setPreviousInstructions] = useState<string[]>(
    persisted.previousInstructions ?? [],
  );
  const [history, setHistory] = useState<AssistHistoryItem[]>(persisted.history ?? []);

  useEffect(() => {
    persistState({ input, messages, previousInstructions, history });
  }, [input, messages, previousInstructions, history]);

  return (
    <AssistContext.Provider
      value={{
        input,
        setInput,
        messages,
        setMessages,
        loading,
        setLoading,
        previousInstructions,
        setPreviousInstructions,
        history,
        setHistory,
      }}
    >
      {children}
    </AssistContext.Provider>
  );
};
